import React, { createContext, useContext, useState, ReactNode } from 'react'

interface HeaderContextType {
  headerExtra: ReactNode | null
  setHeaderExtra: (content: ReactNode | null) => void
  headerRight: ReactNode | null
  setHeaderRight: (content: ReactNode | null) => void
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined)

export function HeaderProvider({ children }: { children: ReactNode }) {
  const [headerExtra, setHeaderExtra] = useState<ReactNode | null>(null)
  const [headerRight, setHeaderRight] = useState<ReactNode | null>(null)

  return (
    <HeaderContext.Provider
      value={{ headerExtra, setHeaderExtra, headerRight, setHeaderRight }}
    >
      {children}
    </HeaderContext.Provider>
  )
}

export function useHeader() {
  const context = useContext(HeaderContext)
  if (!context) {
    throw new Error('useHeader must be used within a HeaderProvider')
  }
  return context
}

/**
 * Hook to set header extra content (left side) that cleans up on unmount
 */
export function useHeaderExtra(
  content: ReactNode | null,
  deps: React.DependencyList = [],
) {
  const { setHeaderExtra } = useHeader()

  React.useEffect(() => {
    setHeaderExtra(content)
    return () => setHeaderExtra(null)
  }, deps)
}

/**
 * Hook to set header right content that cleans up on unmount
 */
export function useHeaderRight(
  content: ReactNode | null,
  deps: React.DependencyList = [],
) {
  const { setHeaderRight } = useHeader()

  React.useEffect(() => {
    setHeaderRight(content)
    return () => setHeaderRight(null)
  }, deps)
}
